<template>
  <div>
    <ts-loading-banner :loading="loading">
      <div class="item-list-shell">
        <div class="item-list-toolbar">
          <div class="item-list-title">{{ $t('preInvoice.item') }}</div>
          <div class="item-list-hint">
            {{ notAvailable ? $t('preInvoice.itemSelectAcademicHint') : $t('preInvoice.itemReadyHint') }}
          </div>
        </div>

        <div class="item-table-wrap">
          <table class="ts-table item-table">
            <thead>
              <tr>
                <th class="col-fee-type">{{ $t('preInvoice.feeType') }}</th>
                <th class="col-fee-name">{{ $t('preInvoice.feeName') }}</th>
                <th class="col-description">{{ $t('preInvoice.description') }}</th>
                <th class="col-date">{{ $t('preInvoice.dateFrom') }}</th>
                <th class="col-date">{{ $t('preInvoice.dateTo') }}</th>
                <th class="col-small text-center">{{ $t('preInvoice.qty') }}</th>
                <th class="col-amount">{{ $t('preInvoice.fee') }}</th>
                <th class="col-discount-type text-center">{{ $t('preInvoice.discountType') }}</th>
                <th class="col-discount text-center">{{ $t('preInvoice.discount') }}</th>
                <th class="col-small text-center">{{ $t('preInvoice.vat') }}</th>
                <th class="col-amount">{{ $t('preInvoice.amount') }}</th>
                <th class="col-action"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="model.inv_detail_list.length == 0">
                <td colspan="12" class="item-empty text-center">
                  <div class="item-empty-icon">
                    <i class="fa fa-list-ul"></i>
                  </div>
                  <div class="item-empty-title">
                    {{ notAvailable ? $t('preInvoice.itemInitialTitle') : $t('preInvoice.itemEmptyTitle') }}
                  </div>
                  <div class="item-empty-hint">
                    {{ notAvailable ? $t('preInvoice.itemSelectAcademicHint') : $t('preInvoice.itemReadyHint') }}
                  </div>
                </td>
              </tr>
              <tr v-for="(item, index) in model.inv_detail_list" :key="index">
                <td>
                  <a-select
                    v-model="item.fee_type_id"
                    showSearch
                    :disabled="notAvailable"
                    style="width: 100%"
                    :options="
                      getFeeTypeLists(item.fee_type_id).map(el => ({
                        value: el.fee_type_id,
                        label: el.fee_type_name
                      }))
                    "
                    optionFilterProp="children"
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                    @change="onFeeTypeChange(item)"
                  />
                  <div
                    class="validate"
                    v-if="
                      errors.has('inv_detail_list.' + index + '.fee_type_id')
                    "
                  >
                    {{
                      errors.first('inv_detail_list.' + index + '.fee_type_id')
                    }}
                  </div>
                </td>
                <td>
                  <a-select
                    v-model="item.fee_setup_id"
                    showSearch
                    style="width: 100%"
                    :maxTagTextLength="30"
                    :options="getFeeSetupByFeeType(item.fee_type_id)"
                    optionFilterProp="children"
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                    @change="onFeeSetupChange(item)"
                  />
                  <div
                    class="validate"
                    v-if="
                      errors.has('inv_detail_list.' + index + '.fee_setup_id')
                    "
                  >
                    {{
                      errors.first('inv_detail_list.' + index + '.fee_setup_id')
                    }}
                  </div>
                </td>
                <td>
                  <input
                    type="text"
                    class="form-control item-control"
                    v-model="item.fee_description"
                  />
                </td>
                <td>
                  <a-date-picker
                    mode="date"
                    v-model="item.fee_start_date"
                    format="DD-MM-YYYY"
                    valueFormat="DD-MM-YYYY"
                    style="width: 100%"
                    @change="startDateChange(item)"
                  />
                </td>
                <td>
                  <a-date-picker
                    mode="date"
                    v-model="item.fee_end_date"
                    format="DD-MM-YYYY"
                    valueFormat="DD-MM-YYYY"
                    style="width: 100%"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    class="form-control item-control text-center"
                    v-model="item.quantity"
                    @input="calculate()"
                  />
                  <div
                    class="validate"
                    v-if="errors.has('inv_detail_list.' + index + '.quantity')"
                  >
                    {{ errors.first('inv_detail_list.' + index + '.quantity') }}
                  </div>
                </td>
                <td>
                  <input
                    type="number"
                    readonly
                    class="form-control item-control item-readonly"
                    v-model="item.fee_amount"
                  />
                </td>
                <td>
                  <a-select
                    v-model="item.discount_type"
                    style="width: 100%"
                    :options="[
                      { value: '$', label: '$' },
                      { value: '%', label: '%' }
                    ]"
                    @change="calculate"
                  />
                  <div
                    class="validate"
                    v-if="
                      errors.has('inv_detail_list.' + index + '.discount_type')
                    "
                  >
                    {{
                      errors.first(
                        'inv_detail_list.' + index + '.discount_type'
                      )
                    }}
                  </div>
                </td>
                <td>
                  <input
                    type="number"
                    class="form-control item-control text-right"
                    v-model="item.discount_rate"
                    @input="calculate()"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    class="form-control item-control text-right"
                    v-model="item.vat_rate"
                    @input="calculate()"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    class="form-control item-control item-readonly text-right"
                    readonly
                    v-model="item.total_amount"
                  />
                  <div
                    class="validate"
                    v-if="
                      errors.has('inv_detail_list.' + index + '.total_amount')
                    "
                  >
                    {{
                      errors.first('inv_detail_list.' + index + '.total_amount')
                    }}
                  </div>
                </td>
                <td class="text-center">
                  <button class="item-remove-button" @click.prevent="removeRow(item)">
                    <i class="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!notAvailable" class="item-add-panel-row">
          <button
            class="item-add-row-button"
            :disabled="notAvailable"
            @click.prevent="addNewRow"
          >
            <i class="fa fa-plus"></i>
            <span>{{ $t('add_new') }}</span>
          </button>
        </div>

        <div class="item-total-bar">
          <span>{{ $t('preInvoice.total') }}</span>
          <strong>{{ formatCurrency(model.total_amount) }}</strong>
        </div>
      </div>
    </ts-loading-banner>
  </div>
</template>

<script>
import TableProgressBar from '@/components/common/TsTable/TableProgressBar'
import { Errors } from 'form-backend-validation'
import { ceil, debounce, round, sumBy } from 'lodash'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'preInvoiceItemList',
  props: ['value', 'studentInfo', 'validate'],
  components: {
    TableProgressBar
  },
  data() {
    return {
      model: {},
      loading: false,
      newRow: {
        fee_type_id: null,
        fee_setup_id: null,
        fee_description: null,
        quantity: 1,
        fee_start_date: null,
        fee_end_date: null,
        payment_term_id: null,
        duration_in_month: 0,
        fee_amount: 0,
        discount_type: '%',
        discount_rate: 0,
        discount_amount: 0,
        discount_reason: null,
        total_amount: 0,
        vat_rate: 0,
        vat_amount: 0,
        amount_include_vat: 0,
        ac_term_id: null,
        grade_id: null,
        shift_id: null
      },
      errors: new Errors(),
      feeTypes: [],
      feeSetupsWithDetail: {}
    }
  },
  created() {
    this.model = this.value
    this.loading = true
    this.getFeeType({
      fields: {
        setup_payment_fee_types:
          'fee_type_id,fee_type_name,default_payment_term_id'
      },
      page: -1
    }).finally(() => (this.loading = false))
  },
  computed: {
    ...mapState('preInvoice', ['room_type_id']),
    notAvailable() {
      return !(
        (this.model.student_id != '') | null &&
        (this.model.grade_id != null) | undefined &&
        (this.model.ac_term_id != null) | undefined
      )
    }
  },
  methods: {
    ...mapActions('preInvoice', [
      'getFeeType',
      'preInvoiceFormView',
    ]),
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(Number(value) || 0)
    },
    addNewRow() {
      if (this.notAvailable) return

      this.model.inv_detail_list.push(_.clone(this.newRow))
    },
    removeRow(row) {
      this.model.inv_detail_list.splice(
        this.model.inv_detail_list.indexOf(row),
        1
      )
      this.model.total_amount = _.sumBy(
        this.model.inv_detail_list,
        'total_amount'
      )
      this.calculate()
    },
    async studentSubscription() {
      this.loading = true
      await this.fetchFeeType()
      let response = await this.preInvoiceFormView({
            ac_status_id: this.model.ac_status_id,
            grade_id: this.model.grade_id,
            ac_term_id: this.model.ac_term_id,
            student_id: this.model.student_id,
            end_date_of_term: this.model.term_stop,
            inv_date: moment().format('DD-MM-YYYY'),
            room_type_id: this.room_type_id,
            fnName: 'getStudentSubscripton',
          })
      this.model.inv_detail_list = []
      await Promise.all(
        Object.values(response.getStudentSubscripton)
          .flat()
          .map(async el => {
            var row = _.clone(this.newRow)
            row.fee_type_id = el.fee_type_id
            row.fee_setup_id = el.fee_setup_id
            const rowDetail = this.model.inv_detail_list.filter(
              d => d.fee_type_id == row.fee_type_id
            ).length

            if (rowDetail == 0) {
              this.model.inv_detail_list.push(row)
              await this.onFeeTypeChange(el, 1)
              if (!this.setDefaultFeeSetup(row)) {
                this.onFeeSetupChange(row)
              }
            }
          })
      )
      if (this.model.inv_detail_list.length == 0) {
        this.model.total_amount = 0
      }
      this.loading = false
    },
    async onFeeTypeChange(feeType, waiting = 0) {
      if (!this.feeSetupsWithDetail.hasOwnProperty(feeType.fee_type_id)) {
        if (!waiting) {
          this.loading = true
        }
        let response = await this.preInvoiceFormView({
            ac_status_id: this.model.ac_status_id,
            grade_id: this.model.grade_id,
            ac_term_id: this.model.ac_term_id,
            student_id: this.model.student_id,
            end_date_of_term: this.model.term_stop,
            fee_type_id: feeType.fee_type_id,
            invoice_date: this.model.pre_inv_date,
            inv_date: moment().format('DD-MM-YYYY'),
            room_type_id: this.room_type_id,
            invoice_id: 0,
            fnName: 'getFeeSetupWithDetail',
          })

        if(!Object.values(response.getFeeSetupWithDetail).length) {
          this.model.inv_detail_list = this.model.inv_detail_list.filter(
            item => item.fee_type_id !== feeType.fee_type_id
          )
        }

        this.feeSetupsWithDetail = Object.assign(
          {},
          this.feeSetupsWithDetail,
          response.getFeeSetupWithDetail
        )
        if (!waiting) {
          this.loading = false
        }
      }

      if (waiting == 0) {
        this.clearAfterFeeTypeSetupChange(feeType)
        this.setDefaultFeeSetup(feeType)
      }
    },
    setDefaultFeeSetup(item) {
      const feeType = this.feeTypes.find(
        feeType => feeType.fee_type_id == item.fee_type_id
      )
      const defaultPaymentTermId = feeType
        ? feeType.default_payment_term_id
        : null

      if (!defaultPaymentTermId) return false

      const matches = (this.feeSetupsWithDetail[item.fee_type_id] || []).filter(
        feeSetup =>
          Number(feeSetup.payment_term_id) === Number(defaultPaymentTermId)
      )

      item.fee_setup_id = matches.length === 1 ? matches[0].fee_setup_id : null

      if (matches.length === 1) {
        this.onFeeSetupChange(item)
      }

      return true
    },
    onFeeSetupChange(item) {
      if(this.feeSetupsWithDetail[item.fee_type_id] == undefined) return []
      this.feeSetupsWithDetail[item.fee_type_id]
        .filter(f => f.fee_setup_id == item.fee_setup_id)
        .forEach(el => {
          item.fee_description = el.fee_name // description is a fee name
          item.quantity = 1
          item.fee_start_date = el.fee_start_date
            ? moment(el.fee_start_date, 'DD-MM-YYYY')
            : null
          item.fee_end_date = el.fee_end_date
            ? moment(el.fee_end_date, 'DD-MM-YYYY')
            : null
          item.payment_term_id = el.payment_term_id
          item.duration_in_month = el.duration_in_month
          item.fee_amount = el.fee_amount
          item.discount_type = '%'
          ;(item.discount_rate = el.discount_rate),
            (item.discount_reason = el.discount_remark)
          item.ac_term_id = el.ac_term_id
          item.grade_id = el.grade_id
          item.shift_id = el.shift_id
        })
      this.calculate()
    },
    clearAfterFeeTypeSetupChange(feeType) {
        feeType.fee_description = ''
        feeType.quantity = 1
        feeType.fee_setup_id = null
        feeType.fee_start_date = null
        feeType.fee_end_date = null
        feeType.payment_term_id = null
        feeType.duration_in_month = null
        feeType.fee_amount = 0
        feeType.discount_type = '%'
        feeType.discount_rate = 0
        feeType.discount_amount = 0
        feeType.discount_reason = null
        feeType.total_amount = 0
        feeType.vat_rate = 0
        feeType.vat_amount = 0
        feeType.amount_include_vat = 0
        feeType.ac_term_id = null
        feeType.grade_id = null
        feeType.shift_id = null
    },
    getFeeSetupByFeeType(feeTypeId) {
      let result = this.feeSetupsWithDetail[feeTypeId]
      if (result != undefined) {
        return result.map(el => ({
          value: el.fee_setup_id,
          id: el.fee_setup_id,
          label: el.fee_name
        }))
      }
      return []
    },
    calculate() {
      let price = 0;
      if (this.model.inv_detail_list.length > 0) {
        this.model.inv_detail_list.forEach(item => {
          price = item.fee_amount ? item.fee_amount * item.quantity : 0
          if (item.discount_type == '%') {
            item.discount_amount = item.discount_rate
              ? price * round(item.discount_rate / 100, 4)
              : 0
          }
          if (item.discount_type == '$') {
            item.discount_amount = item.discount_rate
          }
          item.vat_amount =
            ((price - item.discount_amount) * item.vat_rate) / 100
          item.total_amount =
            ceil(price - item.discount_amount, 2) + item.vat_amount
          item.amount_include_vat = ceil(
            price - item.discount_amount + item.vat_amount,
            2
          )
          this.model.total_amount = sumBy(
            this.model.inv_detail_list,
            'total_amount'
          )
        })
      } else {
        this.model.total_amount = 0
      }
    },
    startDateChange(item) {
      if ([0, 3, 6, 12].includes(item.duration_in_month)) return;

      item.fee_end_date = moment(
        item.fee_start_date.format('DD-MM-YYYY'),
        'DD-MM-YYYY'
      ).add(item.duration_in_month, 'M')
        .subtract(1, 'day'); 
    },
    getFeeTypeLists(currentFeeTypeId) {

    if (!this.model.inv_detail_list || this.model.inv_detail_list.length === 0) {
        return this.feeTypes;
      }

      // Get all fee type IDs except the current one
      const feeTypeIds = this.model.inv_detail_list
        .filter(el => el.fee_type_id !== currentFeeTypeId)
        .map(el => el.fee_type_id);
      
      // Filter out all used fee types except the current one
      return this.feeTypes.filter(feeType => 
        !feeTypeIds.includes(feeType.fee_type_id) || feeType.fee_type_id === currentFeeTypeId
      );
    },
    fetchFeeType(){
      return this.preInvoiceFormView({
            ac_status_id: this.model.ac_status_id,
            grade_id: this.model.grade_id,
            ac_term_id: this.model.ac_term_id,
            student_id: this.model.student_id,
            end_date_of_term: this.model.term_stop,
            fnName: 'feeType',
          })
          .then((response) => {
              this.feeTypes = response.feeType
            })
    }
  },
  watch: {
    'model.grade_id': debounce(function(grade_id) {
      this.feeSetupsWithDetail = []
      if (grade_id != undefined && this.model.ac_term_id) {
        this.studentSubscription()
      } else {
        this.model.inv_detail_list = []
      }
    }),
    'model.ac_term_id': debounce(function(ac_term_id) {
      this.feeSetupsWithDetail = []
      if (ac_term_id != undefined && this.model.grade_id) {
        this.studentSubscription()
      } else {
        this.model.inv_detail_list = []
      }
    }),
    validate: function(error) {
      this.errors = new Errors(error.errors)
    }
  }
}
</script>

<style lang="css" scoped>
.item-list-shell {
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #ffffff;
}
.item-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}
.item-list-title {
  color: #111827;
  font-weight: 600;
}
.item-list-hint {
  margin-top: 2px;
  color: #6b7280;
  font-size: 12px;
}
.item-table-wrap {
  overflow: auto;
}
.item-table {
  width: 100%;
  min-width: 1420px;
}
.item-table > thead > tr > th {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 8px 6px;
  border-bottom: 1px solid #e5e7eb;
  background: #f3f4f6;
  color: #111827;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
}
.item-table > tbody > tr > td {
  padding: 6px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  vertical-align: top;
}
.item-table > tbody > tr:hover > td {
  background: #fbfdff;
}
.col-fee-type,
.col-fee-name,
.col-description {
  width: 190px;
}
.col-date {
  width: 150px;
}
.col-small {
  width: 78px;
}
.col-amount,
.col-discount,
.col-discount-type {
  width: 105px;
}
.col-action {
  width: 48px;
}
.item-add-panel-row {
  padding: 8px 12px;
  border-top: 1px solid #e5e7eb;
  background: #fbfdff;
}
.item-add-row-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 34px;
  border: 1px dashed #93c5fd;
  border-radius: 4px;
  background: #f8fbff;
  color: #1d4ed8;
  font-weight: 600;
}
.item-add-row-button:hover,
.item-add-row-button:focus {
  background: #eff6ff;
  outline: none;
}
.item-control {
  min-height: 32px;
  padding: 5px 8px;
}
.item-readonly {
  background: #f9fafb;
  color: #374151;
}
.item-remove-button {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #dc2626;
}
.item-remove-button:hover,
.item-remove-button:focus {
  background: #fee2e2;
  outline: none;
}
.item-empty {
  padding: 34px 16px !important;
}
.item-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-bottom: 8px;
  border-radius: 50%;
  background: #eef6ff;
  color: #1d68b3;
}
.item-empty-title {
  color: #374151;
  font-weight: 600;
}
.item-empty-hint {
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
}
.item-total-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 10px 12px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #374151;
}
.item-total-bar strong {
  min-width: 120px;
  color: #111827;
  font-size: 16px;
  text-align: right;
}
</style>
